// @flow

import React from "react";
import { localize } from "redux-i18n";
import type { FormProps } from "redux-form";

import { Button, ButtonGroup, Col, Row, Checkbox, Input, ComboBox } from "@components";
import type {
  AlertServiceLengthPeriodType,
  AlertServiceLength,
} from "@modules/service/reducer/alerts/types";
import type { OptionType } from "@components";
import {
  AlertServiceLengthPeriodTypeEnum as PeriodEnum,
  AlertServiceLengthMoreOrLessThanEnum as MoreOrLessThanEnum,
} from "@modules/service/reducer/alerts/constants";
import "./alert-service-length.scss";

const DEFAULT_PERIOD_IN_DAYS = 500;

type OwnProps = $Exact<{
  data: ?AlertServiceLength,
  onChange?: (data: AlertServiceLength) => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
}>;

export class AlertServiceLengthForm extends React.Component<Props> {
  get isEnabled(): boolean {
    return !!this.props.data;
  }

  get label(): React$Node {
    if (!this.isEnabled) {
      return (
        <div>
          {this.props.t("ALERT_DETAILS_SERVICE_LENGTH_RESTRICT_LESS_THAN")}
          &nbsp;
          <span className="text-grey font-weight-bold">
            {this.props.t("ALERT_DETAILS_SERVICE_LENGTH_UNRESTRICTED")}
          </span>
        </div>
      );
    }

    return <div>{this.props.t("ALERT_DETAILS_SERVICE_LENGTH_RESTRICT")}</div>;
  }

  handleCheckboxChange = (e: SyntheticEvent<HTMLInputElement>) => {
    this.props.onChange(
      this.isEnabled
        ? null
        : {
            duration: DEFAULT_PERIOD_IN_DAYS,
            moreOrLessThan: MoreOrLessThanEnum.LESS_THAN,
            periodType: PeriodEnum.DAY,
          },
    );
  };

  handleMoreOrLessThanChange = (val: OptionType) => {
    this.props.onChange({
      ...this.props.data,
      moreOrLessThan: val,
    });
  };

  handleDurationChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    if (isNaN(value)) {
      return;
    }

    this.props.onChange({
      ...this.props.data,
      duration: +value,
    });
  };

  handlePeriodChange = (periodType: AlertServiceLengthPeriodType) => () => {
    this.props.onChange({
      ...this.props.data,
      periodType,
    });
  };

  get lengthValue() {
    return this.props.data.moreOrLessThan === MoreOrLessThanEnum.MORE_THAN
      ? {
          value: MoreOrLessThanEnum.MORE_THAN,
          label: this.props.t(MoreOrLessThanEnum.MORE_THAN),
        }
      : {
          value: MoreOrLessThanEnum.LESS_THAN,
          label: this.props.t(MoreOrLessThanEnum.LESS_THAN),
        };
  }

  get lengthOptions() {
    return [
      {
        value: MoreOrLessThanEnum.LESS_THAN,
        label: this.props.t(MoreOrLessThanEnum.LESS_THAN),
      },
      {
        value: MoreOrLessThanEnum.MORE_THAN,
        label: this.props.t(MoreOrLessThanEnum.MORE_THAN),
      },
    ];
  }

  render() {
    return (
      <Row className="d-flex justify-content-center mb-3">
        <Col sm={12} className="d-flex flex-columns">
          <Checkbox
            id="k"
            className="mr-3"
            label={this.label}
            checked={this.isEnabled}
            onChange={this.handleCheckboxChange}
          />
          {this.isEnabled ? (
            <React.Fragment>
              <ComboBox
                onChange={this.handleMoreOrLessThanChange}
                className="service-length__select"
                value={this.lengthValue}
                options={this.lengthOptions}
              />
              <Input
                className="mr-3"
                type="number"
                min="1"
                value={this.props.data.duration}
                onChange={this.handleDurationChange}
                style={{ width: "100px" }}
              />
              <ButtonGroup className="mb-4">
                <Button
                  color={this.props.data.periodType === PeriodEnum.DAY ? "selected" : null}
                  onClick={this.handlePeriodChange(PeriodEnum.DAY)}
                >
                  {this.props.t("DAYS")}
                </Button>
                <Button
                  color={this.props.data.periodType === PeriodEnum.WEEK ? "selected" : null}
                  onClick={this.handlePeriodChange(PeriodEnum.WEEK)}
                >
                  {this.props.t("WEEKS")}
                </Button>
                <Button
                  color={this.props.data.periodType === PeriodEnum.MONTH ? "selected" : null}
                  onClick={this.handlePeriodChange(PeriodEnum.MONTH)}
                >
                  {this.props.t("MONTHS")}
                </Button>
              </ButtonGroup>
            </React.Fragment>
          ) : null}
        </Col>
      </Row>
    );
  }
}

export default localize()(AlertServiceLengthForm);
